import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import styled from "styled-components";
import { allowedImageTypes } from "../../configs/config";
import { theme } from "../../configs/theme";
import { usePwaViewportWidth } from "../../hooks/useScreenWidth";
import { getCroppedImg, tryCompressImage } from "../../services/compression";
import { Gap, Row } from "../../styles/Utils";
import PictureIcon from "../svgs/icons/Picture.icon";
import { PlusIcon } from "../svgs/icons/Plus.icon";

interface PictureInputProps {
  selectedPictures: (File | string | null)[];
  setSelectedPictures: Dispatch<SetStateAction<(File | string | null)[]>>;
}

export default function PictureInput({ selectedPictures, setSelectedPictures }: PictureInputProps) {
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null); // Create ref for file input

  const PictureListItem = ({ index }: { index: number }) => {
    const image = selectedPictures[index];
    return (
      <PictureListItemContainer onClick={() => setPreviewIndex(index)}>
        {image != null ? (
          <img
            src={typeof image == "string" ? image : URL.createObjectURL(image)}
            alt="Aperçu"
            onClick={() => setPreviewIndex(index)}
          />
        ) : (
          <Row onClick={() => fileInputRef.current?.click()} $justifyContent="center">
            <PictureIcon color={theme.color.tertiaryLight} size="50%" />
          </Row>
        )}

        <RedDot>{index + 1}</RedDot>
      </PictureListItemContainer>
    );
  };

  // const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   const inputMaxFileSize = 5 * 1000 * 1000;
  //   const finalMaxFileSize = 400 * 1000;

  //   try {
  //     if (file) {
  //       // Step 1: Check if the file type is an allowed image type
  //       if (!allowedImageTypes.includes(file.type)) {
  //         alert("Invalid file type! Please select a valid image (JPG, PNG, GIF, WEBP).");
  //         return;
  //       }

  //       // Step 2: Check if the new file is different from the existing one
  //       if (
  //         selectedPictures
  //           .filter((e) => typeof e !== "string")
  //           .map((e) => e?.name)
  //           .includes(file.name)
  //       ) {
  //         alert("You have already selected this image.");
  //         return;
  //       }

  //       // Step 3: Check if the file size is already greater than 10MB
  //       if (file.size > inputMaxFileSize) {
  //         alert("Image larger than 5MB!");
  //         return;
  //       }

  //       // Step 4: Compress the image and update state
  //       try {
  //         const compressedFile = file.size > finalMaxFileSize ? await tryCompressImage(file, finalMaxFileSize) : file;

  //         // Set the compressed image to the selected pictures
  //         setSelectedPictures((prev) => {
  //           const updatedPictures = [...prev]; // Clone array
  //           updatedPictures[previewIndex] = compressedFile as File; // Update specific index
  //           return updatedPictures;
  //         });
  //       } catch (err) {
  //         console.log("Error compressing the image", err);
  //         alert("Image is too large even after compression!");
  //       }
  //     }
  //   } finally {
  //     if (fileInputRef.current) {
  //       fileInputRef.current.value = "";
  //     }
  //   }
  // };

  const imagePreview = selectedPictures[previewIndex];

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showCropScreen, setShowCropScreen] = useState(false);

  const screenWidth = usePwaViewportWidth();

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputMaxFileSize = 10 * 1000 * 1000;
    const file = e.target.files?.[0];
    try {
      if (file) {
        // Step 1: Check if the file type is an allowed image type
        if (!allowedImageTypes.includes(file.type)) {
          alert("Invalid file type! Please select a valid image (JPG, PNG, WEBP).");
          return;
        }

        // Step 2: Check if the file size is already greater than 10MB
        if (file.size > inputMaxFileSize) {
          alert("Image larger than 10MB!");
          return;
        }

        // Step 3: Show the cropper
        const url = URL.createObjectURL(file);
        setImageSrc(url);
        setShowCropScreen(true);
      }
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleDone = async () => {
    const finalMaxFileSize = 400 * 1000;
    if (imageSrc && croppedAreaPixels) {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const fileSizeInBytes = croppedBlob.size;

      console.log("Cropped file size:", fileSizeInBytes / 1000, "KB");

      try {
        const compressedFile =
          croppedBlob.size > finalMaxFileSize
            ? await tryCompressImage(croppedBlob as File, finalMaxFileSize)
            : croppedBlob;

        // Set the compressed image to the selected pictures
        setSelectedPictures((prev) => {
          const updatedPictures = [...prev]; // Clone array
          updatedPictures[previewIndex] = compressedFile as File; // Update specific index
          return updatedPictures;
        });
      } catch (err) {
        console.log("Error compressing the image", err);
        alert("Image is too large even after compression!");
      }

      // You can convert it to base64 or keep as Blob
      // const reader = new FileReader();
      // reader.readAsDataURL(croppedBlob);
      // reader.onloadend = () => {
      //   const base64data = reader.result as string;
      //   setFinalImage(base64data);
      // };

      resetCropperSettings();
    }
  };

  const handleCancelCrop = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    resetCropperSettings();
  };

  const resetCropperSettings = () => {
    setShowCropScreen(false);
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Lock scroll when cropping
  useEffect(() => {
    if (showCropScreen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showCropScreen]);

  return (
    <>
      <PicturePreview onClick={() => fileInputRef.current?.click()}>
        <HiddenInput ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} />
        {imagePreview != null ? (
          <img
            src={typeof imagePreview == "string" ? imagePreview : URL.createObjectURL(imagePreview)}
            alt="Aperçu"
            onClick={() => setPreviewIndex(previewIndex)}
          />
        ) : (
          <PlusIcon color={theme.color.tertiaryLight} />
        )}
      </PicturePreview>
      <Gap $height={1.5} />
      <Row $gap={1}>
        <PictureListItem index={0} />
        <PictureListItem index={1} />
        <PictureListItem index={2} />
        <PictureListItem index={3} />
      </Row>
      {showCropScreen && imageSrc && (
        <Overlay>
          <Modal $width={screenWidth}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <ButtonContainer>
              <Row $justifyContent="center" $gap={1}>
                <Button onClick={() => handleDone()}>✅ Ok</Button>
                <Button onClick={() => handleCancelCrop()}>❌ Cancel</Button>
              </Row>
            </ButtonContainer>
          </Modal>
        </Overlay>
      )}
    </>
  );
}

const ButtonContainer = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
`;

const Button = styled.button`
  background: ${(props) => props.theme.color.primary};
  color: ${(props) => props.theme.color.tertiaryLight};
  padding: 10px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
`;

const Modal = styled.div<{
  $width: number;
}>`
  position: relative;
  width: ${(props) => props.$width + "px"};
  height: 70%;
`;

const Overlay = styled.div`
  z-index: 1;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  background: rgba(0, 0, 0, 1);
  flex-direction: column;
  align-items: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const RedDot = styled(Row)`
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  aspect-ratio: 1;
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
  background-color: ${(props) => props.theme.color.tertiary};
  border-radius: 100px;
  color: ${(props) => props.theme.color.tertiaryLight};
  font-family: ${(props) => props.theme.font.family.label};
`;

const PictureListItemContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3dvh;
  width: 100%;
  aspect-ratio: 1;
  background-color: ${(props) => props.theme.color.primary};
  overflow: hidden svg {
    fill: ${(props) => props.theme.color.primaryDark};
    height: 50%;
  }
  img {
    height: 100%;
    width: 100%;
    border-radius: 3dvh;
  }
`;

const PicturePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 30px;
  width: 100%;
  height: 18rem;
  background-color: ${(props) => props.theme.color.primary};
  overflow: hidden;
  svg {
    height: 30%;
  }

  img {
    height: 100%;
    width: 100%;
  }
`;
